import {
  AltTransportFill18Icon,
  AltTransportOutline18Icon,
  BusFill18Icon,
  BusOutline18Icon,
  TrainFill18Icon,
  TrainOutline18Icon,
} from "@vygruppen/spor-icon-react";
import {
  getLocalStorageVehicleAffectedSidePanelSettings,
  setLocalStorageVehicleAffectedSidePanelSettings,
} from "api/dropsLocalStorage";
import CollapseButton from "features/CenterContent/shared/Tabs/CollapseButton";
import { Tabs, TabsSides } from "features/CenterContent/shared/Tabs/TabsSides";
import AffectedTrains from "features/VehicleSidebar/AffectedTrains/AffectedTrains";
import { useAffectedTrains } from "features/VehicleSidebar/AffectedTrains/useAffectedTrains";
import VehicleList from "features/VehicleSidebar/VehicleList/AddVehicles/VehicleList";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CollapsibleWrapper } from "shared/components/CollapsibleWrapper";
import { Box, Flex } from "shared/components/Spor";
import {
  InfrastructureIncidentIcon18,
  InfrastructureIncidentIconTabsActive,
} from "shared/icons/InfrastructureIncidentIcon18";
import { useDropsRole } from "stores/useDropsRole";
import { useSidePanelsState } from "stores/useSidePanelsState";
import styled from "styled-components";

export const CloseButtonWrapper = styled.div`
  grid-area: close;
  position: relative;
  display: flex;
  align-items: flex-end;
  height: 3rem;
  border-radius: 6px;
  padding: 3px 5px;
  z-index: 1;
`;

const CustomBox = styled(Box)`
  width: 100%;
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
`;

const VehicleWorkList = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useSidePanelsState((state) => [
    state.trainListOpen,
    state.setTrainListOpen,
  ]);

  useEffect(() => {
    setOpen(getLocalStorageVehicleAffectedSidePanelSettings());
  }, [setOpen]);

  useEffect(() => {
    setLocalStorageVehicleAffectedSidePanelSettings(open);
  }, [open]);

  const affectedTrainsResult = useAffectedTrains();
  const { data } = affectedTrainsResult;

  const affectedTrainsCount = data?.affectedTrains.length;

  const trainTabs: Tabs = {
    id: "trainList",
    iconActive: <TrainFill18Icon />,
    iconInactive: <TrainOutline18Icon />,
    text: t("Togliste"),
  };

  const affectedTrainTabs: Tabs = {
    id: "affectedTrains",
    iconActive: <InfrastructureIncidentIconTabsActive />,
    iconInactive: <InfrastructureIncidentIcon18 />,
    text: t("Arbeidsliste"),
  };

  const busTabs: Tabs[] = [
    {
      id: "busList",
      iconActive: <BusFill18Icon />,
      iconInactive: <BusOutline18Icon />,
      text: t("Buss"),
    },
    {
      id: "reserveBusList",
      iconActive: <AltTransportFill18Icon />,
      iconInactive: <AltTransportOutline18Icon />,
      text: t("Reservebuss"),
    },
  ];

  const [selectedTabIds, setSelectedTabIds] = useState(["trainList"]);

  const { isVaktlederOrDriftledningen, isTrafikkplanleggerOrOKS } =
    useDropsRole();
  const showAffectedTrains = isVaktlederOrDriftledningen();
  const showBus = isTrafikkplanleggerOrOKS();
  // Switch to trainList at role switch
  useEffect(() => {
    setSelectedTabIds(["trainList"]);
  }, [showAffectedTrains, showBus]);

  const tabs = [
    trainTabs,
    ...(showAffectedTrains ? [affectedTrainTabs] : []),
    ...(showBus ? busTabs : []),
  ];

  const notifications = useMemo(
    () =>
      showAffectedTrains
        ? { affectedTrains: affectedTrainsCount ?? 0 }
        : undefined,
    [showAffectedTrains, affectedTrainsCount],
  );

  const [newTrainAlarm, setNewTrainAlarm] = useState(
    affectedTrainsCount !== undefined ? affectedTrainsCount > 0 : false,
  );

  const lastSeenCount = useRef<number | undefined>(undefined);

  useEffect(() => {
    const current = data?.affectedTrains.length ?? 0;
    if (
      lastSeenCount.current !== undefined &&
      current > lastSeenCount.current
    ) {
      setNewTrainAlarm(true);
    }
    lastSeenCount.current = current;
  }, [data]);

  // Reset alarm when user opens the tab
  useEffect(() => {
    if (selectedTabIds.includes("affectedTrains")) {
      setNewTrainAlarm(false);
    }
  }, [selectedTabIds]);

  return (
    <CollapsibleWrapper $gridArea="vehicleAffected" $side="left" $open={open}>
      <Flex flexDirection="column" w="100%">
        <Flex justifyContent="space-between" alignItems="center" width="100%">
          <TabsSides
            justifyContent="start"
            tabs={tabs}
            selectedTabIds={selectedTabIds}
            setSelectedTabIds={setSelectedTabIds}
            notifications={notifications}
            alwaysShowNotifications
            notificationSemantics={newTrainAlarm ? "alarm" : "transparent"}
          />
          <CollapseButton
            open={!open}
            setOpen={() => setOpen(!open)}
            title={t("Skjul togliste")}
          />
        </Flex>
        <CustomBox flex={1} minH="0px" position="relative">
          {selectedTabIds.includes("trainList") && (
            <VehicleList vehicleTabsIndex={0} />
          )}
          {selectedTabIds.includes("busList") && (
            <VehicleList vehicleTabsIndex={1} />
          )}
          {selectedTabIds.includes("reserveBusList") && (
            <VehicleList vehicleTabsIndex={2} />
          )}
          {selectedTabIds.includes("affectedTrains") && (
            <AffectedTrains affectedTrainsResult={affectedTrainsResult} />
          )}
        </CustomBox>
      </Flex>
    </CollapsibleWrapper>
  );
};

export default VehicleWorkList;
